// React
import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";

// React Query
import { useQuery } from "@tanstack/react-query";

// Redux
import { useSelector } from "react-redux";

// MUI
import { Grid, IconButton, Tooltip, Typography } from "@mui/material";

// Third Party
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

// Hooks
import { useServices } from "@/PayPlans/hooks/useServices/useServices";
import useCanAssignPayPlan from "../hooks/useCanAssignPayPlan";
import { useTermsAndConditionEdition } from "@/PayPlans/hooks/useTermsAndCondition";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

// Helpers
import { and, or } from "@/Common/helpers/sonar";
import { getPositionDepartment } from "@/Common/helpers/Positions";
import { getSupervisorsByPositionHierarchy } from "@/PayPlans/api/assignments/queries";
import { getSystemSettingsWashoutReviewers } from "@/Washouts/api/common/queries";

// Layouts
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import AppSideBar from "@/Layouts/components/SideBars/AppSideBar";
import MatchesItem from "@/Layouts/components/SideBars/SideSelectorMenu/MatchesItem";
import AssignPayPlanButton from "@/Layouts/components/AssignPayPlanButton";
import SideInformation from "@/PayPlans/Layouts/PayPlansSideBar/SideInformation";
import AssignmentDialog from "../AssignmentDialog";

// Icons
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

// Constants
import { DATE_FORMAT } from "@/Constants/date";
const showBy = "name";
const selectBy = "_id";

// Function
const getMappedMatches = (list = [], available = false) => {
	return list.map((item) => {
		return {
			...item,
			available,
			disabled: !item.pdfAvailability,
			customComponent: {
				disabled: !item.pdfAvailability,
				component: <MatchesItem />,
			},
		};
	});
};

const getMappedMainTree = (payPlansTree = null) => {
	const { currentlyAssigned = [], previouslyAssigned = [], available = [] } = payPlansTree || {};

	const currentList = getMappedMatches(currentlyAssigned);
	const previouslyList = getMappedMatches(previouslyAssigned);
	const availableList = getMappedMatches(available, true);

	return [
		{
			_id: "previouslyAssigned",
			name: "Previously Assigned",
			bold: true,
			root: true,
			disabled: !previouslyList?.length,
			list: previouslyList,
		},
		{
			_id: "currentlyAssigned",
			name: "Currently Assigned",
			bold: true,
			root: true,
			disabled: !currentList?.length,
			list: currentList,
		},
		{
			_id: "available",
			name: "Available for Assignment",
			bold: true,
			root: true,
			disabled: !availableList?.length,
			list: availableList,
		},
	];
};

const getDefaultSelectedItems = (tree = [], selected = "") => {
	const selectedItems = tree?.filter((item) => item._id === selected);
	return selectedItems?.map((item) => item._id);
};

const buildDefaultTree = (defaultPayPlans) => {
	const keyToLabel = {
		newVehiclePayPlan: "NEW",
		preOwnedVehiclePayPlan: "USED",
	};

	if (!defaultPayPlans) {
		return [];
	}

	return Object.keys(defaultPayPlans)
		.map((key) => {
			const available = ![].some((item) => item._id === defaultPayPlans[key]?._id);
			if (available && defaultPayPlans[key]) {
				return {
					...defaultPayPlans[key],
					label: keyToLabel[key] || null,
					available,
				};
			}
			return null;
		})
		.filter(Boolean);
};

const getDefaultMappedTree = (tree = []) => {
	return tree?.map((item) => {
		return {
			...item,
			disabled: !item.pdfAvailability,
			customComponent: {
				disabled: !item.pdfAvailability,
				component: <SideInformation />,
			},
		};
	});
};

const InformationRow = (props) => {
	const { title = "", content = "", sx = null } = props;
	return (
		<Grid container direction="row">
			<Grid item>
				<Typography className="select-plan-dialog-employee-data-title">{title}</Typography>
			</Grid>
			<Grid item>
				<Typography className="select-plan-dialog-employee-data-content" sx={sx}>
					{content}
				</Typography>
			</Grid>
		</Grid>
	);
};
InformationRow.propTypes = {
	title: PropTypes.string,
	content: PropTypes.string,
	sx: PropTypes.object,
};

function defineFirstBusinessPhoneNumber(communications) {
	if (communications && Array.isArray(communications)) {
		const phone = communications.find((item) => item.communicationType === "PHONE" && item.usage === "BUSINESS");
		if (phone) {
			return phone.value;
		}
	}
	return "";
}

const findDefaultSupervisor = (position, reviewers, positionSupervisorsFromHierarchy) => {
	const defaultSupervisor = reviewers?.washoutReviewers?.find((rev) => rev.position === position)?.reviewerId ?? null;
	const positionLower = position.toLowerCase();
	if (!defaultSupervisor) {
		let exclude = null;
		if (positionLower.includes("new")) {
			exclude = "pre-owned";
		} else if (positionLower.includes("pre-owned")) {
			exclude = "new";
		}
		const filteredSupervisorsFromHierarchy = exclude
			? positionSupervisorsFromHierarchy.filter((u) => !u.position.toLowerCase().includes(exclude))
			: positionSupervisorsFromHierarchy;
		return filteredSupervisorsFromHierarchy[0];
	}
	return defaultSupervisor;
};

const getSupervisorByHierarchyAndWashoutReviewer = (positionSupervisorsFromHierarchy, defaultSupervisor) => {
	const exists = positionSupervisorsFromHierarchy.some((user) => user._id === defaultSupervisor._id);
	if (!exists) {
		return [...positionSupervisorsFromHierarchy, defaultSupervisor];
	}

	return positionSupervisorsFromHierarchy;
};

export default function SelectPlanDialog(props) {
	const {
		data = null,
		locationId = "",
		payPlanPosition = "",
		payPlanSelected = "",
		hireDate = "",
		assignPayPlan = () => {},
		closeCallback = () => {},
		updateSelectedPlan = () => {},
		updateUpcomingPayPlan = () => {},
	} = props;

	const [currentAssignment, setCurrentAssignment] = useState([]);
	const [filteredAssignment, setFilteredAssignment] = useState([]);
	const [value, setValue] = useState("");
	const [areaOpen, setAreaOpen] = useState(false);
	const [selected, setSelected] = useState(null);
	const userLogged = useSelector((state) => state.user.user);
	const [defaultSelectedId, setDefaultSelectedId] = useState("");
	const [dataTree, setDataTree] = useState([]);
	const [defaultTree, setDefaultTree] = useState([]);
	const [selectedSupervisor, setSelectedSupervisor] = useState("");

	const payPlanservices = useServices(payPlanPosition, data.isDepartmental ? "DEPARTMENTAL" : "PRODUCER");
	const { payPlanAvailability } = useTermsAndConditionEdition(props?.data?.position);
	const { clearCache } = useCacheCleaner();
	/*********************************************** QUERIES ***********************************************/
	const {
		data: planList,
		isLoading,
		isFetching,
		isIdle,
	} = useQuery({
		queryKey: ["payPlansListDialog", locationId, payPlanPosition, data.isDepartmental],
		queryFn: async () => payPlanservices.queries.getPayPlansSidebarTree(locationId, payPlanPosition, true),
		enabled: !!locationId,
		staleTime: Infinity,
	});
	const department = getPositionDepartment(data?.position);

	const { data: positionSupervisors, isLoading: isLoadingDepartmentsHierarchy } = useQuery({
		queryKey: ["getSupervisorsByPositionHierarchy", data?.position, locationId],
		queryFn: async () => {
			const systemSettingReviewers = await getSystemSettingsWashoutReviewers(locationId ?? "");
			const positionSupervisorsFromHierarchy = await getSupervisorsByPositionHierarchy(locationId, data?.position);
			const defaultSupervisor = findDefaultSupervisor(
				props?.data?.position,
				systemSettingReviewers,
				positionSupervisorsFromHierarchy
			);
			setSelectedSupervisor(defaultSupervisor);
			return getSupervisorByHierarchyAndWashoutReviewer(positionSupervisorsFromHierarchy, defaultSupervisor);
		},
		enabled: and(!!locationId, !!department),
		refetchOnMount: "always",
	});

	/*********************************************** HANDLERS ***********************************************/
	const newDefaultSupervisor = React.useMemo(() => positionSupervisors?.find((sup) => sup?.default) ?? null, [positionSupervisors]);

	const handleDateClick = (date) => {
		const today = moment().format(DATE_FORMAT.monthAbbrDatePadded);
		const input = {
			position: payPlanPosition,
			assignmentTypeGroup: false,
			planId: selected._id,
			assignmentDate: today,
			assignedBy: userLogged._id,
			effectiveDate: date,
			_id: uuidv4(),
			supervisorId: selectedSupervisor?._id,
		};
		assignPayPlan?.(input, selected.title);
	};

	const handleClose = () => {
		closeCallback?.();
	};

	const sideSelection = useCallback(
		function (sel) {
			setSelected(sel);
			if (payPlanSelected) {
				props.payPlanSelected = sel?._id;

				if (sel?._id !== selected?._id) {
					setDefaultSelectedId(sel._id);
				}
			}
		},
		[selected, setSelected]
	);

	const handleChange = () => (event) => {
		setValue(event.target.value);
		const filtered = currentAssignment.filter((employee) => employee.name.toLowerCase().includes(event.target.value.toLowerCase()));
		setFilteredAssignment(filtered);
	};

	const clearSearch = () => {
		setValue("");
		setFilteredAssignment(currentAssignment);
	};

	const handleAreaToggle = () => {
		setAreaOpen(!areaOpen);
	};

	useEffect(() => {
		if (payPlanAvailability) {
			const { pdfAvailability, _id, locationId: subsLocationId } = payPlanAvailability;
			if (locationId === subsLocationId) {
				const disableTreeNode = (treeNode) => {
					return {
						...treeNode,
						list: treeNode.list.map((treeNodeListItem) => {
							let pdfAvailabilityAux = treeNodeListItem.pdfAvailability;

							if (!_id || _id === treeNodeListItem._id) {
								pdfAvailabilityAux = pdfAvailability;
							}
							return {
								...treeNodeListItem,
								disabled: !pdfAvailabilityAux,
								pdfAvailability: pdfAvailabilityAux,
								customComponent: {
									...treeNodeListItem.customComponent,
									disabled: !pdfAvailabilityAux,
									pdfAvailability: pdfAvailabilityAux,
								},
							};
						}),
					};
				};

				const disableDefaultTreeNode = (treeDefaultNode) => {
					let pdfAvailabilityDefaultAux = treeDefaultNode.pdfAvailability;

					if (!_id || _id === treeDefaultNode._id) {
						pdfAvailabilityDefaultAux = pdfAvailability;
					}
					return {
						...treeDefaultNode,
						disabled: !pdfAvailabilityDefaultAux,
						pdfAvailability: pdfAvailabilityDefaultAux,
						customComponent: {
							...treeDefaultNode.customComponent,
							disabled: !pdfAvailabilityDefaultAux,
							pdfAvailability: pdfAvailabilityDefaultAux,
						},
					};
				};

				setDataTree((previousDataTree) => previousDataTree.map(disableTreeNode));
				setDefaultTree((previousDefaultDataTree) => previousDefaultDataTree.map(disableDefaultTreeNode));
				clearCache(["htmlPayPlanView"]);
				setSelected(null);
			}
		}
	}, [payPlanAvailability]);

	useEffect(() => {
		if (!props.locationId) {
			return;
		}

		if (props.employeesList) {
			setCurrentAssignment(props.employeesList);
			setFilteredAssignment(props.employeesList);
		}
	}, [props]);

	useEffect(() => {
		if (planList) {
			const mainTree = getMappedMainTree(planList);
			setDataTree(mainTree);

			const defaultPayPlansTree = buildDefaultTree(planList.defaultPayPlans);
			setDefaultTree(defaultPayPlansTree);
		}

		return () => {
			setDataTree([]);
		};
	}, [planList]);

	useEffect(() => {
		if (newDefaultSupervisor && !selectedSupervisor) {
			setSelectedSupervisor(newDefaultSupervisor);
		}
	}, [newDefaultSupervisor, selectedSupervisor]);

	const handleTreeItemClick = (items = []) => {
		if (items?.length) {
			const itemSelected = items[0];
			handleSelection(itemSelected);
		}
	};

	const handleSelection = (selItem) => {
		sideSelection(selItem);
	};

	const hasPermission = useCanAssignPayPlan(data?.position);

	const tooltip = getAssignedButtonTooltip(selected, selectedSupervisor, data?.position);

	return (
		<Grid container direction="column" sx={{ width: 1250, height: 808 }}>
			<Grid item className="select-plan-dialog-header !h-auto">
				<IconButton
					aria-label="close"
					sx={{
						position: "absolute",
						top: "10px",
						right: "10px",
					}}
					onClick={handleClose}
				>
					<CloseRoundedIcon />
				</IconButton>
				<Grid container direction="column" className="!m-[25px] !w-auto">
					<Grid item>
						<Typography className="select-plan-dialog-employee-name">{data?.fullName}</Typography>
					</Grid>
					<Grid item>
						<Grid container direction="row">
							<Grid item>
								<Grid
									container
									direction="column"
									className="select-plan-dialog-employee-data"
									sx={{ width: "450px", mr: "20px" }}
								>
									<Grid item>
										<InformationRow title={"Position:"} content={data?.position} />
									</Grid>
									<Grid item>
										<InformationRow title={"Employee ID:"} content={data?.employeeId} />
									</Grid>

									<Grid item className="!mt-[24px]">
										<InformationRow
											title={"Supervisor:"}
											content={
												<SmartSelector
													loading={isLoadingDepartmentsHierarchy}
													tagsVariant="text"
													classes="!w-[130px] !mt-[-4px]"
													showBy={"fullName"}
													selectBy={"_id"}
													selected={selectedSupervisor}
													options={positionSupervisors || []}
													inputProps={{
														placeholder: "Supervisor",
													}}
													listBoxProps={{
														sx: { maxHeight: "223px" },
													}}
													popperProps={{
														sx: { minWidth: "190px", maxWidth: "240px", zIndex: "9999 !important" },
													}}
													error={false}
													onChangeAction={(sel) => {
														setSelectedSupervisor(sel);
													}}
												/>
											}
										/>
									</Grid>
								</Grid>
							</Grid>
							<Grid item>
								<Grid
									container
									direction="column"
									className="select-plan-dialog-employee-data"
									sx={{ width: "460px", mr: "90px" }}
								>
									<Grid item>
										<InformationRow
											title={"Email:"}
											content={data?.email}
											sx={{
												textOverflow: "ellipsis",
												overflow: "hidden",
												whiteSpace: "normal",
												maxWidth: "350px",
											}}
										/>
									</Grid>
									<Grid item>
										<InformationRow
											title={"Phone number:"}
											content={defineFirstBusinessPhoneNumber(data?.communications)}
										/>
									</Grid>
									<Grid item className="!mt-[24px]">
										<InformationRow
											title={"Corporate Sign Off:"}
											content={
												<SmartSelector
													multiple
													loading={false}
													showBy={"label"}
													inputProps={{
														placeholder: "Optional",
													}}
													classes="!w-[130px] !mt-[-4px]"
													selectBy="value"
												/>
											}
										/>
									</Grid>
								</Grid>
							</Grid>
							<Grid item>
								<Grid
									container
									item
									justifyContent="flex-end"
									alignItems="flex-end"
									sx={{ width: "180px", height: "100%" }}
								>
									<Tooltip arrow title={tooltip}>
										<div>
											<AssignPayPlanButton
												data={data}
												selected={selected}
												hireDate={hireDate}
												disabled={!hasPermission || !selected || !selectedSupervisor}
												onDateChanged={(date) => handleDateClick(date)}
												updateSelectedPlan={updateSelectedPlan}
												updateUpcomingPayPlan={updateUpcomingPayPlan}
											/>
										</div>
									</Tooltip>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
			<Grid item sx={{ height: "610px" }}>
				<Grid container direction="row" sx={{ height: "100%" }}>
					<Grid item sx={{ width: "230px", height: "100%" }}>
						<AppSideBar
							searchProps={{
								searchBy: showBy,
								loading: or(isLoading, isFetching, isIdle),
								onChangeSearchAction: (v) => {
									if (v && selected) {
										setSelected(null);
										setDefaultSelectedId("");
									}
								},
							}}
							appendProps={{
								selectBy,
								showBy,
								label: {
									title: "Default Pay Plans",
									emptyTitle: "No Default Pay Plan Set",
									isEmpty: ({ tree: innerTree }) => !innerTree?.length,
								},
								divider: true,
								selectedItems: getDefaultSelectedItems(defaultTree, defaultSelectedId),
								tree: getDefaultMappedTree(defaultTree),
								loading: or(isLoading, isFetching, isIdle),
								loadingRows: 2,
								onSelectAction: handleTreeItemClick,
							}}
							sideBarProps={{
								selectBy,
								showBy,
								height: "400px",
								tree: dataTree,
								selected: selected?.[selectBy] || "",
								loading: or(isLoading, isFetching, isIdle),
								onSelectAction: sideSelection,
							}}
						/>
					</Grid>
					<AssignmentDialog
						value={value}
						selectedItem={selected}
						areaOpen={areaOpen}
						locationId={locationId}
						filteredAssignment={filteredAssignment}
						currentPayPlanId={data?.currentPayPlanAssignment?.planId?._id}
						clearSearch={clearSearch}
						handleAreaToggle={handleAreaToggle}
						handleChange={handleChange}
					/>
				</Grid>
			</Grid>
		</Grid>
	);
}

function getAssignedButtonTooltip(selectedPayPlan, selectedSupervisor, userPosition) {
	if (!selectedPayPlan) {
		return "Please select a pay plan to assign";
	}

	if (!selectedSupervisor) {
		return "Please select a Supervisor for this assignment";
	}

	return "";
}

SelectPlanDialog.propTypes = {
	data: PropTypes.object,
	locationId: PropTypes.string,
	payPlanPosition: PropTypes.string,
	payPlanSelected: PropTypes.string,
	hireDate: PropTypes.string,
	employeesList: PropTypes.array,
	assignPayPlan: PropTypes.func,
	closeCallback: PropTypes.func,
	updateSelectedPlan: PropTypes.func,
	updateUpcomingPayPlan: PropTypes.bool,
};
